'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import { ConversationPreview, BotInspirationFeed } from '../shared';
import { WelcomeSection } from '../WelcomeSection';
import { FeatureHighlights } from '../FeatureHighlights';
import { HelpChat } from '../HelpChat';
import { VideoHero } from '../VideoHero';
import { useInspirationFeedEnabled } from '../../../lib/posthogABTesting';
import type { BotIdea } from '../content/botIdeas';
import type { ReactNode } from 'react';

// Hebrew marketing video (Story: marketing-video-2026-05-06).
// Files live under apps/creator/public/videos/. Update these paths when the
// final MP4 and poster are dropped in place. See public/videos/README.md.
const HE_VIDEO_SRC = '/videos/marketing-he-1080p.mp4';
const HE_VIDEO_POSTER = '/videos/marketing-he-poster.jpg';

/**
 * Variant C: Split Screen Desktop Optimized
 *
 * Layout: 50/50 split with tabbed conversation examples (left) + creation form (right)
 * Target: Desktop productivity users with multiple files to upload
 *
 * This variant is closest to the existing LandingLayout but adds tabbed
 * conversation examples to demonstrate versatility.
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface VariantCProps {
  /** The content input form as children */
  children: ReactNode;
  /** Whether value props are collapsed (user started creating) */
  collapsed?: boolean;
  /** Content to show on the left side when collapsed */
  leftContent?: ReactNode;
  /** Callback when a bot idea chip is clicked (Story 6.13) */
  onIdeaClick?: (idea: BotIdea) => void;
}

type TabValue = 'tournament' | 'handyman' | 'event';

export function VariantC({
  children,
  collapsed = false,
  leftContent,
  onIdeaClick,
}: VariantCProps) {
  const t = useTranslations('creator.landing');
  const locale = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<TabValue>('tournament');
  const { enabled: showInspirationFeed } = useInspirationFeedEnabled();

  // Hebrew is the only locale with a finished marketing video for now.
  // When EN/ES ship, gate can be replaced with a per-locale asset map.
  const showMarketingVideo = locale === 'he';

  // Mobile layout: Single column with compact value props
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Collapsible value proposition */}
        <Collapse in={!collapsed} timeout={300}>
          <Box sx={{ mb: 1, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, p: 2 }}>
            <WelcomeSection compact />
          </Box>
        </Collapse>

        {/* Hebrew marketing video — mobile */}
        {showMarketingVideo && (
          <Collapse in={!collapsed} timeout={300} unmountOnExit>
            <VideoHero src={HE_VIDEO_SRC} poster={HE_VIDEO_POSTER} />
          </Collapse>
        )}

        {/* Inspiration feed — mobile (Story 6.13) */}
        <Collapse in={!collapsed && showInspirationFeed} timeout={300} unmountOnExit>
          <BotInspirationFeed variant="horizontal" onIdeaClick={onIdeaClick} pageVariant="C" feedPosition="above-tabbed-examples" />
        </Collapse>

        {/* Creation form with visual grouping */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 0.5 }}
          >
            {t('contentSection.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t('contentSection.subtitle')}
          </Typography>
          {children}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block', textAlign: 'center' }}
          >
            {t('contentSection.helperText')}
          </Typography>
        </Box>

        {/* Note: leftContent is NOT shown on mobile - UnifiedContentPanel handles the list */}

        {/* Help chat at bottom (only when not collapsed) */}
        <Collapse in={!collapsed} timeout={300}>
          <HelpChat />
        </Collapse>
      </Box>
    );
  }

  // Desktop layout: 50/50 split with tabbed examples
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 6,
        width: '100%',
        minHeight: '70vh',
        alignItems: 'flex-start',
      }}
    >
      {/* Left column: Tabbed examples OR sources list when collapsed */}
      {collapsed && leftContent ? (
        <Box
          sx={{
            flex: '0 0 380px',
            maxWidth: 380,
            position: 'sticky',
            top: 24,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >
          {leftContent}
        </Box>
      ) : (
        <Box
          sx={{
            flex: collapsed ? 0 : '1 1 480px',
            minWidth: collapsed ? 0 : 300,
            maxWidth: collapsed ? 0 : 480,
            overflow: 'hidden',
            transition: 'flex 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
            visibility: collapsed ? 'hidden' : 'visible',
            opacity: collapsed ? 0 : 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              position: 'sticky',
              top: 16,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 3,
              px: 3,
              py: 2,
              maxWidth: 480,
              boxSizing: 'border-box',
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ fontSize: { md: '1.25rem', lg: '1.5rem' }, mb: 0.5 }}
              >
                {t('welcome.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 400, mx: 'auto' }}
              >
                {t('variants.c.subtitle')}
              </Typography>
            </Box>

            {/* Inspiration feed (Story 6.13) */}
            <Collapse in={!collapsed && showInspirationFeed} timeout={300} unmountOnExit>
              <BotInspirationFeed variant="vertical" onIdeaClick={onIdeaClick} pageVariant="C" feedPosition="above-tabbed-examples" />
            </Collapse>

            {/* Marketing video (Hebrew) OR tabbed conversation examples (everywhere else) */}
            {showMarketingVideo ? (
              <VideoHero src={HE_VIDEO_SRC} poster={HE_VIDEO_POSTER} />
            ) : (
              <TabbedExamples
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}

            {/* Feature highlights */}
            <FeatureHighlights variant="compact" />

            {/* Help chat */}
            <HelpChat />
          </Box>
        </Box>
      )}

      {/* Right column: Creation form with visual grouping */}
      <Box
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          maxWidth: collapsed && leftContent ? 600 : collapsed ? 700 : 600,
          mx: collapsed && !leftContent ? 'auto' : 0,
          transition: 'max-width 0.3s ease, margin 0.3s ease',
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          boxShadow: 1,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={600}
          color="primary.main"
          sx={{ mb: 1 }}
        >
          {t('contentSection.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t('contentSection.subtitle')}
        </Typography>
        {children}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          {t('contentSection.helperText')}
        </Typography>
      </Box>
    </Box>
  );
}

interface TabbedExamplesProps {
  activeTab: TabValue;
  onTabChange: (tab: TabValue) => void;
}

function TabbedExamples({ activeTab, onTabChange }: TabbedExamplesProps) {
  const t = useTranslations('creator.landing.variants.c.tabs');

  const tabs: { value: TabValue; label: string }[] = [
    { value: 'tournament', label: t('tournament.label') },
    { value: 'handyman', label: t('handyman.label') },
    { value: 'event', label: t('event.label') },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => onTabChange(newValue as TabValue)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
          />
        ))}
      </Tabs>

      {/* Tab content */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
        >
          {t(`${activeTab}.description`)}
        </Typography>
        <ConversationPreview
          maxMessages={4}
          showSourceHints={true}
          maxHeight={220}
          messages={getMessagesForTab(activeTab, t)}
        />
      </Box>
    </Box>
  );
}

/**
 * Get sample messages for each tab
 */
function getMessagesForTab(
  tab: TabValue,
  t: ReturnType<typeof useTranslations>
) {
  const messages = {
    tournament: [
      { id: 't1', text: t('tournament.messages.user1'), isUser: true },
      { id: 't2', text: t('tournament.messages.bot1'), isUser: false, sourceHint: t('tournament.sourceHint') },
      { id: 't3', text: t('tournament.messages.user2'), isUser: true },
      { id: 't4', text: t('tournament.messages.bot2'), isUser: false, sourceHint: t('tournament.sourceHint') },
      { id: 't5', text: t('tournament.messages.user3'), isUser: true },
      { id: 't6', text: t('tournament.messages.bot3'), isUser: false, sourceHint: t('tournament.sourceHint') },
    ],
    handyman: [
      { id: 'h1', text: t('handyman.messages.user1'), isUser: true },
      { id: 'h2', text: t('handyman.messages.bot1'), isUser: false, sourceHint: t('handyman.sourceHint') },
      { id: 'h3', text: t('handyman.messages.user2'), isUser: true },
      { id: 'h4', text: t('handyman.messages.bot2'), isUser: false, sourceHint: t('handyman.sourceHint') },
      { id: 'h5', text: t('handyman.messages.user3'), isUser: true },
      { id: 'h6', text: t('handyman.messages.bot3'), isUser: false, sourceHint: t('handyman.sourceHint') },
    ],
    event: [
      { id: 'e1', text: t('event.messages.user1'), isUser: true },
      { id: 'e2', text: t('event.messages.bot1'), isUser: false, sourceHint: t('event.sourceHint') },
      { id: 'e3', text: t('event.messages.user2'), isUser: true },
      { id: 'e4', text: t('event.messages.bot2'), isUser: false, sourceHint: t('event.sourceHint') },
      { id: 'e5', text: t('event.messages.user3'), isUser: true },
      { id: 'e6', text: t('event.messages.bot3'), isUser: false, sourceHint: t('event.sourceHint') },
    ],
  };

  return messages[tab];
}
